import _ from 'lodash';
import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { selectors as classSelectors } from 'edrolo/reducers/class';
import { selectors as progressSelectors } from 'edrolo/reducers/progress';
import { reindexMapFromIds } from 'edrolo/reducers/utils';
import { buildAnonymizer, getIsAnonymized } from 'edrolo/domains/anonymizer';

const createClassSelectors = _.memoize((classId) => {
  const getClassDetail = createSelector(
    [classSelectors.getAllClassDetail],
    (allClassDetail) => classId && allClassDetail.get(classId)
  );

  const getClassEnrolments = createSelector(
    [classSelectors.getAllClassEnrolments, getClassDetail],
    (allClassEnrolments, classDetail) =>
      classDetail && allClassEnrolments
        ? allClassEnrolments.filter((enrolment) =>
            classDetail.get('enrolments').includes(enrolment.get('id'))
          )
        : Immutable.Map()
  );

  const getClassEnrolmentProfileIds = createSelector(
    [getClassEnrolments],
    (enrolments) => enrolments.map((enrolment) => enrolment.get('profile'))
  );

  const getClassEnrolmentProfiles = createSelector(
    [
      getClassEnrolmentProfileIds,
      classSelectors.getAllProfiles,
      getIsAnonymized,
    ],
    (enrolmentIds, allProfiles, isAnonymized) => {
      const profiles =
        enrolmentIds &&
        allProfiles
          .filter((profile) =>
            enrolmentIds.includes(profile && profile.get('id'))
          )
          .toList();

      const anonymizeProfile = buildAnonymizer({
        seedResolver: 'user',
        schemaFields: [
          { key: 'last_name', type: 'last_name' },
          { key: 'name', type: 'full_name' },
        ],
      });
      return isAnonymized ? profiles.map(anonymizeProfile) : profiles;
    }
  );

  const getClassCourseProgress = createSelector(
    [progressSelectors.getAllClassProgress],
    (allClassCourseProgress) => classId && allClassCourseProgress.get(classId)
  );

  const getClassAveragesByLesson = createSelector(
    [getClassCourseProgress, progressSelectors.getAllClassLessonAverage],
    (classCourseProgress, allClassLessonAverage) => {
      const classLessonAverageIds =
        classCourseProgress && classCourseProgress.get('lessons');
      return reindexMapFromIds(
        classLessonAverageIds,
        allClassLessonAverage,
        'lesson_id'
      );
    }
  );

  // returns all the content for that course indexed by classId
  const getPrivilegedCourseContent = createSelector(
    [classSelectors.getAllPrivilegedContent],
    (allPrivilegedContent) =>
      allPrivilegedContent && allPrivilegedContent.get(`${classId}`)
  );

  const getCourseId = createSelector(
    [getPrivilegedCourseContent],
    (privilegedContent) =>
      privilegedContent && privilegedContent.get('course_id')
  );

  const getTeacherNoteIds = createSelector(
    [getPrivilegedCourseContent],
    (privilegedContent) =>
      privilegedContent && privilegedContent.get('teacher_notes_for_students')
  );

  const getLessonResourceLocks = createSelector(
    [getPrivilegedCourseContent],
    (privilegedContent) =>
      privilegedContent
        ? privilegedContent.get('lesson_resource_switches')
        : Immutable.List()
  );

  const getExamLocks = createSelector(
    [getPrivilegedCourseContent],
    (privilegedContent) =>
      privilegedContent
        ? privilegedContent.get('exams_locked')
        : Immutable.List()
  );

  const getTeacherDiscussionLinksByLessonId = createSelector(
    [getPrivilegedCourseContent],
    (privilegedContent) =>
      privilegedContent && privilegedContent.get('discussion_boards')
  );

  const getTeacherNotesGroupedByLessonId = createSelector(
    [getTeacherNoteIds, classSelectors.getAllTeacherNotes],
    (teacherNoteIds, allTeacherNotes) => {
      return (
        teacherNoteIds &&
        allTeacherNotes &&
        teacherNoteIds
          .map((noteId) => allTeacherNotes.get(noteId.toString()))
          .groupBy((note) => note.get('lesson_id').toString())
      );
    }
  );

  return {
    getCourseId,
    getClassDetail,
    getClassEnrolmentProfiles,
    getClassCourseProgress,
    getClassAveragesByLesson,
    getPrivilegedCourseContent,
    getTeacherDiscussionLinksByLessonId,
    getTeacherNotesGroupedByLessonId,
    getLessonResourceLocks,
    getExamLocks,
    ...classSelectors,
  };
});

export default createClassSelectors;
